import JSZip from 'jszip'

/**
 * Liest die Teilnehmerliste des Gewinnspiels aus einer Datei — im Browser,
 * der Worker bekommt nur die Namen. Excel (.xlsx) wird als ZIP geöffnet und
 * das erste Tabellenblatt gelesen; CSV und Textdateien zeilenweise.
 *
 * Welche Spalte die Instagram-Namen enthält, entscheidet die Kopfzeile: eine
 * Spalte mit „insta“, „handle“, „name“, „nutzer“ oder „user“ im Titel. Ohne
 * passende Kopfzeile gilt die erste Spalte. Alles Weitere (Normalisieren,
 * Doppelte) macht der Worker.
 */

const SPALTEN_HINWEISE = [
  'instagram',
  'insta',
  'handle',
  'benutzer',
  'nutzer',
  'user',
  'name',
  'account',
]

export async function liesTeilnehmerliste(datei: File): Promise<string[]> {
  const name = datei.name.toLowerCase()
  if (name.endsWith('.xlsx') || name.endsWith('.xlsm')) {
    return ausZeilen(await xlsxZeilen(await lies(datei, 'bytes')))
  }
  return ausZeilen(textZeilen(await lies(datei, 'text')))
}

/** Über den FileReader: Der ist überall da, auch wo `File.arrayBuffer()` fehlt (ältere Safari, jsdom). */
function lies(datei: File, als: 'bytes'): Promise<ArrayBuffer>
function lies(datei: File, als: 'text'): Promise<string>
function lies(datei: File, als: 'bytes' | 'text'): Promise<ArrayBuffer | string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as ArrayBuffer | string)
    reader.onerror = () => reject(reader.error ?? new Error('Datei nicht lesbar'))
    if (als === 'bytes') reader.readAsArrayBuffer(datei)
    else reader.readAsText(datei)
  })
}

/** Aus Zeilen mit Spalten die Namensspalte wählen — Kopfzeile erkennen, sonst erste Spalte. */
export function ausZeilen(zeilen: string[][]): string[] {
  const gefuellt = zeilen.filter((z) => z.some((zelle) => zelle.trim() !== ''))
  if (gefuellt.length === 0) return []
  const kopf = gefuellt[0]!.map((z) => z.trim().toLowerCase())
  // Die Hinweise in ihrer Reihenfolge: „Instagram" schlägt „Name", wenn
  // beide Spalten da sind (Name; Instagram → die zweite).
  let spalte = -1
  for (const hinweis of SPALTEN_HINWEISE) {
    spalte = kopf.findIndex((k) => k.includes(hinweis))
    if (spalte >= 0) break
  }
  let ab = 1
  if (spalte < 0) {
    spalte = 0
    // Keine Kopfzeile erkannt: Die erste Zeile ist dann schon ein Name —
    // außer sie sieht offensichtlich nach Überschrift aus (enthält Leerzeichen).
    ab = /\s/.test(kopf[0] ?? '') ? 1 : 0
  }
  return gefuellt
    .slice(ab)
    .map((z) => (z[spalte] ?? '').trim())
    .filter((z) => z !== '')
}

/** CSV/Text: Komma, Semikolon oder Tab als Trenner, Anführungszeichen entfernt. */
export function textZeilen(text: string): string[][] {
  return text
    .replace(/^\uFEFF/, '')
    .split(/\r?\n/)
    .map((zeile) =>
      zeile.split(/[;,\t]/).map((zelle) =>
        zelle
          .trim()
          .replace(/^"(.*)"$/, '$1')
          .trim(),
      ),
    )
}

/**
 * Das erste Blatt einer .xlsx-Datei als Zeilen. Werte stehen entweder direkt
 * in der Zelle (`<v>`) oder, bei Text, als Verweis in die gemeinsamen
 * Zeichenketten (`t="s"`); Inline-Text (`<is><t>`) kommt ebenfalls vor.
 */
export async function xlsxZeilen(daten: ArrayBuffer): Promise<string[][]> {
  const zip = await JSZip.loadAsync(daten)
  const parser = new DOMParser()

  const gemeinsame: string[] = []
  const sst = zip.file('xl/sharedStrings.xml')
  if (sst) {
    const dok = parser.parseFromString(await sst.async('string'), 'application/xml')
    for (const si of Array.from(dok.getElementsByTagName('si'))) {
      gemeinsame.push(
        Array.from(si.getElementsByTagName('t'))
          .map((t) => t.textContent ?? '')
          .join(''),
      )
    }
  }

  const blattPfad = await erstesBlatt(zip)
  const blatt = blattPfad ? zip.file(blattPfad) : null
  if (!blatt) throw new Error('In der Excel-Datei wurde kein Tabellenblatt gefunden.')
  const dok = parser.parseFromString(await blatt.async('string'), 'application/xml')

  const zeilen: string[][] = []
  for (const row of Array.from(dok.getElementsByTagName('row'))) {
    const zellen: string[] = []
    for (const c of Array.from(row.getElementsByTagName('c'))) {
      const spalte = spaltenIndex(c.getAttribute('r') ?? '')
      const typ = c.getAttribute('t')
      let wert = ''
      if (typ === 's') {
        const v = c.getElementsByTagName('v')[0]?.textContent ?? ''
        wert = gemeinsame[Number(v)] ?? ''
      } else if (typ === 'inlineStr') {
        wert = Array.from(c.getElementsByTagName('t'))
          .map((t) => t.textContent ?? '')
          .join('')
      } else {
        wert = c.getElementsByTagName('v')[0]?.textContent ?? ''
      }
      zellen[spalte] = wert
    }
    zeilen.push(Array.from(zellen, (z) => z ?? ''))
  }
  return zeilen
}

/** Das erste Blatt laut Arbeitsmappe; ohne lesbare Mappe `xl/worksheets/sheet1.xml`. */
async function erstesBlatt(zip: JSZip): Promise<string | null> {
  const rels = zip.file('xl/_rels/workbook.xml.rels')
  const workbook = zip.file('xl/workbook.xml')
  if (rels && workbook) {
    const parser = new DOMParser()
    const wb = parser.parseFromString(await workbook.async('string'), 'application/xml')
    const erstes = wb.getElementsByTagName('sheet')[0]
    const rId =
      erstes?.getAttribute('r:id') ??
      erstes?.getAttributeNS(
        'http://schemas.openxmlformats.org/officeDocument/2006/relationships',
        'id',
      )
    if (rId) {
      const r = parser.parseFromString(await rels.async('string'), 'application/xml')
      const ziel = Array.from(r.getElementsByTagName('Relationship'))
        .find((rel) => rel.getAttribute('Id') === rId)
        ?.getAttribute('Target')
      if (ziel) return ziel.startsWith('/') ? ziel.slice(1) : `xl/${ziel.replace(/^\.\//, '')}`
    }
  }
  return zip.file('xl/worksheets/sheet1.xml') ? 'xl/worksheets/sheet1.xml' : null
}

/** `B7` → 1, `AA3` → 26. */
export function spaltenIndex(ref: string): number {
  const buchstaben = /^[A-Z]+/.exec(ref)?.[0] ?? 'A'
  let n = 0
  for (const b of buchstaben) n = n * 26 + (b.charCodeAt(0) - 64)
  return Math.max(0, n - 1)
}
